import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import { useActiveOrganization } from "@/contexts/ActiveOrganizationContext";
import { organizationApi } from "@/lib/api/services/organization.api";
import { companyApi } from "@/lib/api/services/company.api";

export interface CatalogScopeOptions {
  /**
   * Opt-in. When the user can pick a concept and nothing from the session has
   * fixed one, select the first concept (A to Z) they have access to, instead
   * of leaving the picker empty. A concept the session already fixed (a pinned
   * concept, the active location) always wins over this default.
   */
  defaultToFirstConcept?: boolean;
  /** Seeds the picked organization and concept, e.g. from the page's URL. */
  initialOrganizationId?: number | null;
  initialCompanyId?: number | null;
}

/**
 * Which organization and concept a catalogue screen (recipes, menus) is looking
 * at, and what its create/edit dialogs should open against.
 *
 * Lifted out of `RecipesListing` so the Menus module resolves scope exactly the
 * way the Recipes screen does: internal staff pick an organization and concept,
 * org admins pick a concept, everyone else is pinned to their own.
 */
export function useCatalogScope(options: CatalogScopeOptions = {}) {
  const { defaultToFirstConcept = false, initialOrganizationId, initialCompanyId } = options;
  const { isAuthenticated, hasRole, user } = useAuth();
  const { activeLocation, isLoading: isActiveLocationLoading } = useActiveLocation();
  const { activeOrganization } = useActiveOrganization();

  const isInternalRecipeViewer = hasRole("SUPER_ADMIN") || hasRole("SUPPORT");
  const isOrgAdminRecipeViewer = hasRole("ORG_ADMIN");
  const canSelectCompanyFilter = isInternalRecipeViewer || isOrgAdminRecipeViewer;
  const showRecipeOrgColumn = isInternalRecipeViewer;
  const showRecipeCompanyColumn = isInternalRecipeViewer || isOrgAdminRecipeViewer;

  const userOrganizationId = Number(user?.organization?.id);
  const fixedOrganizationId = Number.isFinite(userOrganizationId) && userOrganizationId > 0
    ? userOrganizationId
    : null;

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(
    fixedOrganizationId ?? initialOrganizationId ?? activeOrganization?.id ?? null,
  );

  const scopedCompanyIds = useMemo(() => {
    const ids = new Set<number>();
    for (const scope of user?.scopes ?? []) {
      if (scope.type === "COMPANY") {
        const parsed = Number(scope.id);
        if (Number.isFinite(parsed) && parsed > 0) ids.add(parsed);
      }
    }
    return Array.from(ids);
  }, [user?.scopes]);

  const singleScopedCompanyId = scopedCompanyIds.length === 1 ? scopedCompanyIds[0] : null;

  const userCompanyId = Number(user?.company?.id);
  const fixedCompanyId =
    !canSelectCompanyFilter && Number.isFinite(userCompanyId) && userCompanyId > 0
      ? userCompanyId
      : null;

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    initialCompanyId ?? activeLocation?.companyId ?? fixedCompanyId ?? singleScopedCompanyId ?? null,
  );

  const effectiveOrganizationId = fixedOrganizationId ?? selectedOrganizationId;

  const shouldShowOrgFilter = isInternalRecipeViewer;
  const shouldShowCompanyFilter = canSelectCompanyFilter;

  const resolvedFilterCompanyId = (() => {
    if (fixedCompanyId) return fixedCompanyId;
    if (selectedCompanyId) return selectedCompanyId;
    if (activeLocation?.companyId) return activeLocation.companyId;
    return undefined;
  })();

  // The concepts the picker offers: this organization's, or all the user can
  // see when there is no organization to narrow by.
  const conceptOrganizationId = isInternalRecipeViewer
    ? effectiveOrganizationId
    : fixedOrganizationId;

  // Only while nothing has chosen a concept. The active location arrives after
  // first render, and it outranks the default, so wait for it.
  const wantsDefaultConcept =
    defaultToFirstConcept &&
    isAuthenticated &&
    canSelectCompanyFilter &&
    !selectedCompanyId &&
    !fixedCompanyId &&
    !singleScopedCompanyId &&
    !activeLocation?.companyId &&
    !isActiveLocationLoading &&
    (!isInternalRecipeViewer || Boolean(conceptOrganizationId));

  const { data: firstConceptPage, isSuccess: firstConceptLoaded } = useQuery({
    queryKey: ["catalog-scope", "first-concept", conceptOrganizationId ?? null],
    queryFn: () =>
      companyApi.paginate({
        page: 1,
        limit: 1,
        sortBy: "name",
        sortOrder: "asc",
        organizationId: conceptOrganizationId ?? undefined,
      }),
    enabled: wantsDefaultConcept,
    staleTime: 60_000,
  });

  const firstConcept = firstConceptPage?.data[0];
  // Adopted during render rather than in an effect, the same way the menu item
  // form adopts its sole concept. `wantsDefaultConcept` goes false once it is
  // set, so this cannot repeat.
  if (wantsDefaultConcept && firstConcept) {
    setSelectedCompanyId(firstConcept.id);
  }
  // Nothing to select: the user has no concept to build menu items for.
  const hasNoConcepts = wantsDefaultConcept && firstConceptLoaded && !firstConcept;

  const isInternalScopeReady =
    !isInternalRecipeViewer ||
    (Boolean(effectiveOrganizationId) && Boolean(resolvedFilterCompanyId));

  const resolvedCreateCompanyId = (() => {
    if (fixedCompanyId) return fixedCompanyId;
    if (selectedCompanyId) return selectedCompanyId;
    if (activeLocation?.companyId) return activeLocation.companyId;
    return singleScopedCompanyId ?? undefined;
  })();

  // What the create/edit dialogs open against. They used to receive
  // `fixedOrganizationId`, which is null for anyone who can filter across
  // organizations — so a super admin got an empty Organization select even
  // though the page behind the dialog was filtered to one. And the resolved
  // concept arrived as `companyId`, which the dialogs read as "no choice here"
  // and so suppressed the Concept field outright. Only a user whose concept is
  // genuinely fixed gets `companyId`; for everyone else it is a default.
  const dialogFixedCompanyId = canSelectCompanyFilter
    ? undefined
    : (resolvedCreateCompanyId ?? undefined);
  const dialogDefaultCompanyId = resolvedCreateCompanyId ?? null;

  const { data: organizations = [] } = useQuery({
    queryKey: ["recipes", "org-lookup"],
    queryFn: () => organizationApi.getAll(),
    enabled: isAuthenticated && showRecipeOrgColumn,
    staleTime: 60_000,
  });

  const organizationNamesById = useMemo(
    () =>
      Object.fromEntries(
        organizations.map((org) => [org.id, org.name]),
      ) as Record<number, string>,
    [organizations],
  );

  const selectedOrganizationDisplayName =
    (effectiveOrganizationId ? organizationNamesById[effectiveOrganizationId] : undefined) ??
    activeOrganization?.name ??
    user?.organization?.name;

  return {
    isAuthenticated,
    isInternalRecipeViewer,
    showRecipeOrgColumn,
    showRecipeCompanyColumn,
    fixedOrganizationId,
    fixedCompanyId,
    selectedCompanyId,
    setSelectedCompanyId,
    hasNoConcepts,
    /** True when the screen always has a concept, so the picker offers no "clear". */
    conceptAlwaysSelected: defaultToFirstConcept,
    setSelectedOrganizationId,
    effectiveOrganizationId,
    shouldShowOrgFilter,
    shouldShowCompanyFilter,
    resolvedFilterCompanyId,
    isInternalScopeReady,
    dialogFixedCompanyId,
    dialogDefaultCompanyId,
    organizationNamesById,
    selectedOrganizationDisplayName,
  };
}

export type CatalogScope = ReturnType<typeof useCatalogScope>;
